/**
 * Palette d'une application autonome, dérivée de la couleur du module.
 *
 * Cabaza habille ses écrans d'un dégradé violet → rose. Une app autonome garde la même
 * forme de dégradé dans les teintes de son module : la couleur choisie dans l'admin, puis
 * une seconde teinte voisine (même saturation, teinte décalée, un peu plus claire).
 *
 * (index.html embarque une copie minimale de ce calcul pour l'écran de chargement, qui
 * s'affiche avant tout JavaScript : toute modification ici doit y être reportée.)
 */

const HEX = /^#([0-9a-f]{6})$/i;

export const estCouleurValide = (valeur) => HEX.test(String(valeur || '').trim());

const hexVersHsl = (hex) => {
    const n = parseInt(hex.slice(1), 16);
    const r = ((n >> 16) & 255) / 255;
    const g = ((n >> 8) & 255) / 255;
    const b = (n & 255) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    if (d === 0) return { h: 0, s: 0, l };
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    return { h: h * 60, s, l };
};

const hslVersHex = ({ h, s, l }) => {
    const k = (n) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    const hex = (x) => Math.round(x * 255).toString(16).padStart(2, '0');
    return `#${hex(f(0))}${hex(f(8))}${hex(f(4))}`;
};

/**
 * @param {string} couleur - Couleur du module (#RRGGBB)
 * @returns {{primary: string, secondary: string, soft: string}|null} null si la couleur est invalide
 *   - primary   : la couleur du module
 *   - secondary : teinte voisine pour le second bout du dégradé
 *   - soft      : fond très clair (page publique)
 */
export const paletteApp = (couleur) => {
    const c = String(couleur || '').trim();
    if (!HEX.test(c)) return null;

    const { h, s, l } = hexVersHsl(c.toLowerCase());
    const secondary = hslVersHex({ h: (h + 18) % 360, s, l: Math.min(l + 0.10, 0.55) });
    const soft = hslVersHex({ h, s: Math.min(s, 0.6), l: 0.96 });

    return { primary: c.toLowerCase(), secondary, soft };
};
